import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  TOURIST_PAYMENT_MOMENT_LABELS,
  type TouristPaymentMoment,
} from "@/lib/touristPayment";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

export function TouristPaymentSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [moment, setMoment] = useState<TouristPaymentMoment>(
    settings.tourist_payment?.moment ?? "check_out",
  );
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMoment(settings.tourist_payment?.moment ?? "check_out");
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/tourist-payment", { moment });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Touristen-Abrechnung konnte nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Touristen: Zeitpunkt der Abrechnung"
      description="Wann für Gäste vom Typ Tourist automatisch ein Rechnungsentwurf geöffnet wird. Bei Vorkasse entsteht eine Vollrechnung mit allen Buchungspositionen; bei Abreise eine Voll- oder Schlussrechnung, sofern noch keine Aufenthaltsrechnung existiert."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          {(Object.keys(TOURIST_PAYMENT_MOMENT_LABELS) as TouristPaymentMoment[]).map((value) => (
            <label
              key={value}
              className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5"
            >
              <input
                type="radio"
                name="tourist_payment_moment"
                className="mt-1"
                checked={moment === value}
                onChange={() => setMoment(value)}
                disabled={!canWrite}
              />
              <span className="text-sm text-label">{TOURIST_PAYMENT_MOMENT_LABELS[value]}</span>
            </label>
          ))}
        </fieldset>
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Touristen-Abrechnung gespeichert."
          error={error}
          label="Touristen-Abrechnung speichern"
        />
      </form>
    </Card>
  );
}
